import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, EventEmitter, input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActiveElement, Chart, ChartConfiguration, ChartData, ChartEvent, ChartItem, registerables, TooltipItem } from 'chart.js';


/**
 * Component for pie chart of dashboard.
 */
@Component({
	selector: 'tsp-pie-chart',
	templateUrl: './pie-chart.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PieChartComponent implements OnInit, AfterViewInit, OnDestroy {

	chartId = input.required<string>();
	title = input('');
	value = input([0, 0]);
	legend = input(['', '']);
	@Output() selected: EventEmitter<number> = new EventEmitter<number>();

	label1 = computed(() => this._label(0));
	label2 = computed(() => this._label(1));
	hasValue = computed(() => this.value()?.some(v => !!v));

	chart?: Chart<'pie', number[]>;

	readonly COLOR1 = '#baf0bf';
	readonly COLOR2 = '#e8664f';

	private readonly CHART_OPTIONS = {
		scales: { y: { beginAtZero: true, display: false } },
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					title: (item: TooltipItem<'pie'>[]) => '',
					label: (item: TooltipItem<'pie'>) => item.label
				}
			}
		},
		onClick: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
			if (elements.length) {
				this.selected.emit(elements[0].index);
			}
		},
		onHover: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
			chart.canvas.style.cursor = elements.length ? 'pointer' : 'default';
		}
	};


	ngOnInit(): void {
		Chart.register(...registerables);
		Chart.defaults.color = 'white';
	}

	ngAfterViewInit(): void {
		this._createChart();
	}

	private readonly _chartUpdate = effect(() => this.chart ? this._updateChart() : this._createChart());

	private _createChart(): void {
		if (!this.chart && this.hasValue()) {
			const data: ChartData<'pie', number[]> = {
				labels: [this.label1(), this.label2()],
				datasets: [{
					backgroundColor: [this.COLOR1, this.COLOR2],
					borderColor: [this.COLOR1, this.COLOR2],
					data: [this.value()[0], this.value()[1]]
				}]
			};

			const config: ChartConfiguration<'pie', number[]> = {
				type: 'pie',
				data: data,
				options: this.CHART_OPTIONS
			};

			const chartItem: ChartItem = document.getElementById(this.chartId()) as ChartItem;

			if (chartItem) {
				this.chart = new Chart(chartItem, config);
			}
		}
	}

	private _updateChart(): void {
		if (this.chart && this.hasValue()) {
			this.chart.data.datasets[0].data = [...this.value()];
			this.chart.data.labels = [this.label1(), this.label2()];
			this.chart.update();
		} else {
			this.chart = undefined;
		}
	}

	private _label(index: number): string {
		return `${this.legend()[index]} (${this.value()[index]})`;
	}


	ngOnDestroy(): void {
		this.chart?.destroy();
	}
}